import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Tooltip.
 *
 * Shadcn convention exposes Provider/Root/Trigger/Content. For
 * the common "wrap a target with a label" case the `<Tooltip>`
 * compound below handles all four with one prop. Reach for the
 * primitives when you need finer control (controlled state,
 * delayed trigger, etc).
 */

export const TooltipProvider = TooltipPrimitive.Provider;
export const TooltipRoot = TooltipPrimitive.Root;
export const TooltipTrigger = TooltipPrimitive.Trigger;

export const TooltipContent = forwardRef<
  ElementRef<typeof TooltipPrimitive.Content>,
  ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    className={cn(
      'z-50 overflow-hidden rounded-sm border border-neutral-800 bg-popover px-2 py-1 text-[11px] text-popover-foreground',
      'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
      'data-[side=bottom]:slide-in-from-top-1 data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1 data-[side=top]:slide-in-from-bottom-1',
      className,
    )}
    {...props}
  />
));
TooltipContent.displayName = 'TooltipContent';

/**
 * Compound `<Tooltip label="...">child</Tooltip>` — the 80%-case
 * sugar. Wraps the four primitives in one call. Mount a single
 * `<TooltipProvider>` near the app root if you use many of these
 * (we do it in App.tsx).
 */
interface TooltipProps {
  label: ReactNode;
  children: ReactNode;
  // 'top' is the friendly default for table cells; 'right' for
  // sidebar icons; 'bottom' for header buttons.
  side?: 'top' | 'right' | 'bottom' | 'left';
  delay?: number;
}

export function Tooltip({ label, children, side = 'top', delay = 200 }: TooltipProps) {
  return (
    <TooltipPrimitive.Root delayDuration={delay}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side={side}>{label}</TooltipContent>
    </TooltipPrimitive.Root>
  );
}
